<template>
  <el-container class="campaign-card">
    <el-main class="shadow">
      <el-card>
        <template #header>
          <div class="header-container" :class="deviceClass">
            <div class="flex-box">
              <span class="campaign-id"> {{ campaign.id }}. </span>
              <span class="campaign-name">
                {{ campaign.name }}
              </span>
              <el-tag type="danger" :hit="true" size="small">
                Flat deal
              </el-tag>
              <StreamIcon class="stream-icon" :stream-id="campaign.stream" />
              <AdTypeLabel :type="campaign.type_id" />
              <BannerSize
                v-if="AdType.Banner === campaign.type_id"
                :size="campaign.size"
                class="ml-1"
              />
              <DeviceIcon
                v-for="id in campaign.device_ids"
                :key="id"
                :device-id="id"
                class="ml-1"
              />
              <AvailableTrafficLabel
                v-if="campaign.max_traffic > 0"
                v-model="campaign.max_traffic"
              />
              <CreativeState
                v-if="creative"
                class="ml-1"
                :state="creative.advert_state"
                :reason="creative.status_change_reason"
                :edit-url="`/campaigns/${creative.campaign_id}/creatives/${creative.id}/edit`"
              />
              <ViolationsButton
                v-if="creative && isStaff"
                :aggressive="creative.aggressive"
                @click="showMalwareModal"
              />
            </div>
            <div class="flex-box">
              <FlatDealState
                v-if="creative"
                class="mr-1"
                :state="creative.deal_status"
              />
              <el-tooltip
                class="item"
                effect="dark"
                placement="top-start"
                content="View"
              >
                <router-link
                  :to="`/direct_deals/${campaign.id}/edit`"
                  title="View"
                >
                  <el-button
                    type="primary"
                    plain
                    circle
                    size="small"
                    icon="ViewElementIcon"
                    class="small-action-btn"
                  />
                </router-link>
              </el-tooltip>
            </div>
          </div>
        </template>
      </el-card>
    </el-main>
  </el-container>
</template>

<script lang="ts">
import { defineComponent, computed, inject } from 'vue'
import { useCampaignsStore } from '@/apps/traforama/store/campaigns'
import { useCreativesStore } from '@/apps/traforama/store/creatives'
import useResize from '@/hooks/useResize'
import { AdType } from '@/api/types'
import StreamIcon from '@/components/StreamIcon.vue'
import CreativeState from './CreativeState.vue'
import FlatDealState from './FlatDealState.vue'
import BannerSize from '@/apps/traforama/components/BannerSize.vue'
import DeviceIcon from '@/apps/traforama/components/DeviceIcon.vue'
import AdTypeLabel from '@/apps/traforama/components/AdTypeLabel.vue'
import AvailableTrafficLabel from './AvailableTrafficLabel.vue'
import ViolationsButton from '@/apps/traforama/components/ViolationsButton.vue'
import { useUserStore } from '@/store/modules/user'

export default defineComponent({
  name: 'FlatDealRow',
  components: {
    StreamIcon,
    BannerSize,
    CreativeState,
    FlatDealState,
    DeviceIcon,
    AdTypeLabel,
    AvailableTrafficLabel,
    ViolationsButton
  },
  props: {
    campaignId: {
      type: String,
      required: true
    },
    pulsating: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const campaignsStore = useCampaignsStore()
    const creativesStore = useCreativesStore()
    const userStore = useUserStore()

    const { isStaff } = userStore
    const { deviceClass } = useResize()

    const campaignObj = computed(() => {
      return campaignsStore.items[props.campaignId]
    })

    const campaign = computed(() => {
      return campaignObj.value?.attributes
    })

    const creative = computed(() => {
      const campaign = campaignObj.value
      if (!campaign) return

      const creativeId = campaign.relationships?.creatures?.data?.[0]?.id
      if (!creativeId) return

      return creativesStore.items[creativeId]?.attributes
    })

    const creativeId = computed(() => {
      return campaignObj.value?.relationships?.creatures?.data?.[0]?.id
    })

    const openMalwareModal = inject('openMalwareModal')

    const showMalwareModal = () => {
      if (openMalwareModal && creative.value && creativeId.value) {
        openMalwareModal({ id: creativeId.value, name: creative.value.name })
      }
    }

    const checked = computed({
      get: () => {
        return campaignsStore.selected[props.campaignId] !== undefined
      },
      set: (checked: boolean) => {
        campaignsStore.ToggleSelection(props.campaignId, checked)
      }
    })

    const closed = computed({
      get: () => {
        return campaignsStore.closed[props.campaignId] !== undefined
      },
      set: (closed: boolean) => {
        campaignsStore.ToggleClose({ id: props.campaignId, isClosed: closed })
      }
    })

    return {
      campaign,
      deviceClass,
      checked,
      AdType,
      isStaff,
      closed,
      creative,
      showMalwareModal
    }
  }
})
</script>

<style lang="scss" scoped>
.header-container {
  display: flex;
  justify-content: space-between;
  align-items: center;

  .mr-1 {
    margin-right: 1em;
  }

  &.mobile {
    flex-direction: column;
    align-items: flex-start;
  }
}

.flex-box {
  display: flex;
  align-items: center;
}

.campaign-id {
  color: var(--el-text-color-secondary);
  font-size: 12px;
}

.el-tag {
  margin-left: 5px;
  border: none !important;
}

.stream-icon {
  margin-left: 0.5em;
  margin-right: 0.5em;
  color: var(--el-text-color-secondary);
}

.campaign-card {
  margin-bottom: 10px;

  :deep(.el-card) {
    &:hover {
      box-shadow: -4px 4px 9px -1px var(--el-color-primary-light-5);
    }
  }

  :deep(.el-card__body) {
    padding: 0 5px;
  }

  .el-main {
    padding-bottom: 2px;
    padding-top: 2px;
  }

  :deep(.creatives-list) {
    transition: height 1s ease-out;
    opacity: 0;
    height: 0;
    overflow: hidden;
  }

  :deep(.creatives-list.active) {
    opacity: 1;
    height: auto;
    transition: height 1s;
  }

  :deep(.campaign-name) {
    margin-right: 0.5em;
    font-weight: 600;
    font-size: 14px;
  }

  .empty {
    padding: 5px;
    color: var(--el-text-color-secondary);
  }
}
</style>
